/**
 * "Discount Code Price" offers, client side.
 *
 * Single source of truth for every JS surface that renders the
 * "$X with code CODE" line: product-price.js (PDP, quick add),
 * product-card.js (Liquid card swatches), the Constructor rec and cart-rail
 * scripts, collection-tabs-carousel.js and, bundled by Vite through the
 * `@theme` alias, the React PLP (src/product-grid).
 *
 * Twin of snippets/promo-resolve.liquid — keep the rules identical:
 *   - a rule applies when its scope is 'sitewide', or 'tag' and the product
 *     carries that tag; Constructor records carry no tags, so a tag-scoped rule
 *     also matches when the product's collection handles (Constructor
 *     group_ids) include the rule's automated collection (or, when none is
 *     configured, a handle equal to the tag)
 *   - percent: round(price - price * pct / 100); amount: price - round(amt * 100);
 *     the result must satisfy 0 < discounted < price
 *   - lowest discounted price wins; ties go to the lowest slot; never stack
 *
 * Rules arrive once per page as JSON in <script id="promo-rules-json">,
 * emitted by snippets/promo-rules-json.liquid from layout/theme.liquid. The
 * rendered line carries data-promo-slot; assets/base.css maps the slot to the
 * offer colour, so no colour travels through here.
 *
 * Nothing below touches the DOM at import time, so
 * test-harness/shared/promo-rules-harness.mjs can import this file under Node.
 */

export const PROMO_RULES_SCRIPT_ID = 'promo-rules-json';

/**
 * @typedef {object} PromoRule
 * @property {number} slot 1-4
 * @property {'sitewide'|'tag'} scope
 * @property {string} tag Product tag for 'tag' scope
 * @property {string} collectionHandle Automated-collection handle for Constructor surfaces
 * @property {'percent'|'amount'} type
 * @property {number} percent
 * @property {number} amount Store-currency units (dollars), not cents
 * @property {string} code
 * @property {string} message Plain text containing %code% (escaped when rendered)
 * @property {boolean} hidePrice Show the message only (deals with no single price); value still ranks
 * @property {boolean} ctaPrice Price the PDP / quick view Add to Bag button with this offer when it wins
 */

/**
 * @typedef {object} PromoResolution
 * @property {number} slot
 * @property {number} discountedCents
 * @property {string} code
 * @property {string} message
 * @property {boolean} hidePrice
 * @property {boolean} ctaPrice The winning offer prices the buy button (never with hidePrice)
 * @property {PromoRule} rule
 */

/**
 * Coerce whatever the JSON block held into well-typed rules, dropping junk.
 * @param {unknown} input
 * @returns {PromoRule[]}
 */
export function normalizePromoRules(input) {
  if (!Array.isArray(input)) return [];
  /** @type {PromoRule[]} */
  const rules = [];
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const r = /** @type {Record<string, unknown>} */ (raw);
    const slot = Number(r.slot);
    const code = String(r.code ?? '').trim();
    if (!Number.isInteger(slot) || slot < 1 || !code) continue;
    const scope = r.scope === 'tag' ? 'tag' : r.scope === 'sitewide' ? 'sitewide' : null;
    if (!scope) continue;
    const tag = String(r.tag ?? '').trim();
    if (scope === 'tag' && !tag) continue;
    const type = r.type === 'amount' ? 'amount' : 'percent';
    rules.push({
      slot,
      scope,
      tag,
      collectionHandle: String(r.collectionHandle ?? '').trim(),
      type,
      percent: Number(r.percent) || 0,
      amount: Number(r.amount) || 0,
      code,
      message: String(r.message ?? '') || 'with code %code%',
      hidePrice: r.hidePrice === true,
      ctaPrice: r.ctaPrice === true,
    });
  }
  return rules;
}

/**
 * @param {string} text
 * @returns {PromoRule[]} Empty on any parse error.
 */
export function parsePromoRulesJson(text) {
  try {
    return normalizePromoRules(JSON.parse(text));
  } catch {
    return [];
  }
}

/** @type {PromoRule[] | null} */
let cachedRules = null;
/** @type {unknown} */
let cachedDoc = null;

/**
 * Rules from the page's #promo-rules-json block, parsed once and cached.
 * @param {{ getElementById(id: string): { textContent: string | null } | null } | undefined} [doc]
 * @returns {PromoRule[]}
 */
export function readPromoRules(doc = globalThis.document) {
  if (!doc) return [];
  if (cachedRules && cachedDoc === doc) return cachedRules;
  const el = doc.getElementById(PROMO_RULES_SCRIPT_ID);
  cachedRules = el ? parsePromoRulesJson(el.textContent || '') : [];
  cachedDoc = doc;
  return cachedRules;
}

export function resetPromoRulesCache() {
  cachedRules = null;
  cachedDoc = null;
}

/**
 * Comma-separated attribute value (data-product-tags) to a trimmed list.
 * @param {unknown} value
 * @returns {string[]}
 */
export function parseListAttr(value) {
  return String(value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Scope check only (no price).
 * @param {PromoRule} rule
 * @param {{ tags?: string[], collectionHandles?: string[] }} [product]
 */
export function ruleAppliesTo(rule, { tags, collectionHandles } = {}) {
  if (!rule) return false;
  if (rule.scope === 'sitewide') return true;
  if (rule.scope !== 'tag' || !rule.tag) return false;
  const tagList = Array.isArray(tags) ? tags : [];
  if (tagList.some((t) => String(t) === rule.tag)) return true;
  const handles = Array.isArray(collectionHandles) ? collectionHandles : [];
  const wanted = rule.collectionHandle || rule.tag;
  return handles.some((h) => String(h) === wanted);
}

/**
 * Discounted price in cents for one rule, or null when the rule does not
 * reduce the price to a positive amount.
 * @param {PromoRule} rule
 * @param {number} priceCents
 * @returns {number | null}
 */
export function applyPromoRule(rule, priceCents) {
  const price = Number(priceCents);
  if (!rule || !Number.isFinite(price) || price <= 0) return null;
  let discounted;
  if (rule.type === 'percent') {
    if (!(rule.percent > 0)) return null;
    discounted = Math.round(price - (price * rule.percent) / 100);
  } else if (rule.type === 'amount') {
    if (!(rule.amount > 0)) return null;
    discounted = price - Math.round(rule.amount * 100);
  } else {
    return null;
  }
  return discounted > 0 && discounted < price ? discounted : null;
}

/**
 * The single offer to show for one product at one price.
 * @param {PromoRule[]} rules
 * @param {{ priceCents: number, tags?: string[], collectionHandles?: string[] }} product
 * @returns {PromoResolution | null}
 */
export function resolvePromo(rules, { priceCents, tags, collectionHandles } = { priceCents: 0 }) {
  if (!Array.isArray(rules) || !rules.length) return null;
  /** @type {PromoResolution | null} */
  let best = null;
  for (const rule of rules) {
    if (!ruleAppliesTo(rule, { tags, collectionHandles })) continue;
    const discountedCents = applyPromoRule(rule, priceCents);
    if (discountedCents == null) continue;
    const better =
      !best ||
      discountedCents < best.discountedCents ||
      (discountedCents === best.discountedCents && rule.slot < best.slot);
    if (better) {
      best = {
        slot: rule.slot,
        discountedCents,
        code: rule.code,
        message: rule.message,
        hidePrice: Boolean(rule.hidePrice),
        ctaPrice: Boolean(rule.ctaPrice) && !rule.hidePrice,
        rule,
      };
    }
  }
  return best;
}

/**
 * Per-product eligibility for the "Show the discounted price in the Add to Bag
 * button" setting (product_price_discount_cta_price), read off the buy-buttons
 * root: snippets/buy-buttons-v2.liquid and blocks/buy-buttons.liquid stamp
 * data-promo-cta-price="true" (PDP and quick view, edit mode included) with the
 * same data-product-tags <product-price> carries, and variant-picker-v2
 * #applySiblingPromoState rewrites it on a combined-listing sibling swap.
 * null = the button keeps the regular price.
 * @param {DOMStringMap | Record<string, string | undefined> | undefined} dataset
 * @returns {{ tags: string[] } | null}
 */
export function readPromoCtaScope(dataset) {
  if (dataset?.promoCtaPrice !== 'true') return null;
  return { tags: parseListAttr(dataset.productTags) };
}

/**
 * Price to show in an Add to Bag button (PDP, its sticky bar, quick view): the
 * winning offer's discounted price × quantity, when THAT offer has "Show this
 * offer's discounted price in the Add to Bag button" on. null when the button
 * should keep the regular price — no offer applies, the winning offer does
 * not price the button (a losing offer's flag never counts: its price is not
 * what the code gives), the offer hides its price (no single price to
 * promise), or the quantity is not a positive integer. Twin of the Liquid in snippets/buy-buttons-v2.liquid (CTA SSR):
 * parts[7] == '1' there is promo.ctaPrice here (already false with hidePrice).
 * @param {PromoRule[]} rules
 * @param {{ priceCents: number, tags?: string[], collectionHandles?: string[], quantity?: number }} product
 * @returns {number | null}
 */
export function promoCtaPriceCents(rules, { priceCents, tags, collectionHandles, quantity = 1 }) {
  const qty = Number(quantity);
  if (!Number.isInteger(qty) || qty < 1) return null;
  const promo = resolvePromo(rules, { priceCents, tags, collectionHandles });
  if (!promo || !promo.ctaPrice || promo.hidePrice) return null;
  return promo.discountedCents * qty;
}

/**
 * @typedef {object} PromoScope
 * @property {PromoRule[]} rules
 * @property {string[]} collectionHandles Constructor group_ids the tag scope is matched against
 */

/**
 * Offers for a Constructor-rendered surface (recs, cart rails, tabs carousel):
 * the page's rules, to be narrowed per product with promoScopeFor. null =
 * nothing to show — Liquid only stamps data-show-discount-code-price on the
 * host element when the feature is on.
 * @param {DOMStringMap | Record<string, string | undefined> | undefined} dataset
 * @returns {PromoScope | null}
 */
export function readPromoScope(dataset) {
  if (dataset?.showDiscountCodePrice !== 'true') return null;
  const rules = readPromoRules();
  return rules.length ? { rules, collectionHandles: [] } : null;
}

/**
 * The scope narrowed to one product's Constructor group_ids (collection
 * handles) — Constructor records carry no tags, so each tag-scoped offer names
 * an automated collection and is matched through these.
 * @param {PromoScope | null} scope
 * @param {unknown} groupIds
 * @returns {PromoScope | null}
 */
export function promoScopeFor(scope, groupIds) {
  if (!scope) return null;
  return { rules: scope.rules, collectionHandles: Array.isArray(groupIds) ? groupIds.map(String) : [] };
}

/** @param {unknown} value */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Plain-text pieces around %code%, for renderers that build nodes (React).
 * @param {string} message
 * @returns {{ before: string, after: string, hasCode: boolean }}
 */
export function splitPromoMessage(message) {
  const msg = String(message ?? '');
  const i = msg.indexOf('%code%');
  if (i < 0) return { before: msg, after: '', hasCode: false };
  return { before: msg.slice(0, i), after: msg.slice(i + 6), hasCode: true };
}

/**
 * Escaped message HTML with the code wrapped in `.discount-code`, matching
 * snippets/price-discount-code-line.liquid (plain-text message, escaped; only
 * the .discount-code span is markup).
 * @param {string} message
 * @param {string} code
 */
export function renderPromoMessage(message, code) {
  const { before, after, hasCode } = splitPromoMessage(message);
  const codeHtml = hasCode && code ? `<span class="discount-code">${escapeHtml(code)}</span>` : '';
  return escapeHtml(before) + codeHtml + escapeHtml(after);
}

/** Modifier class of an invisible line that only reserves the row's space. */
export const PROMO_LINE_PLACEHOLDER_CLASS = 'price__discount-code-line--placeholder';

/**
 * A real "$X with code" line, never the invisible placeholder product-card.js
 * inserts to reserve the row (that one has no offer behind it and must not be
 * hidden, shown, remembered or rewritten as if it were the live line).
 */
export const REAL_PROMO_LINE_SELECTOR = `.price__discount-code-line:not(.${PROMO_LINE_PLACEHOLDER_CLASS})`;

/**
 * The full line markup. `formattedAmount` is already money-formatted by the
 * caller, so each surface keeps its own formatter.
 *
 * `placeholder` renders the line as an invisible reservation (product-card.js
 * uses it so a card whose displayed colour has no offer still holds the row a
 * sibling colour's line will need): the modifier class hides it (base.css),
 * aria-hidden keeps it out of the accessibility tree, and none of the
 * data-promo-* hooks are stamped, so nothing that reads a real line — the slot
 * colour, hide-price, quick-add's CTA price — can pick the ghost up.
 * @param {PromoResolution | null} promo
 * @param {string} formattedAmount
 * @param {{ placeholder?: boolean }} [options]
 */
export function buildPromoLineHtml(promo, formattedAmount, { placeholder = false } = {}) {
  if (!promo) return '';
  const hide = Boolean(promo.hidePrice);
  // data-promo-cta-price marks a line whose offer also prices the buy button
  // (quick-add.js copies this line's amount into the modal button).
  const ctaPrice = Boolean(promo.ctaPrice) && !hide;
  const openTag = placeholder
    ? `<span class="price__discount-code-line ${PROMO_LINE_PLACEHOLDER_CLASS}" aria-hidden="true">`
    : `<span class="price__discount-code-line" data-promo-slot="${Number(promo.slot) || 0}"` +
      (hide ? ' data-promo-hide-price="true"' : '') +
      (ctaPrice ? ' data-promo-cta-price="true"' : '') +
      '>';
  return (
    openTag +
    (hide ? '' : `<span class="price__discount-code-amount">${escapeHtml(formattedAmount)}</span> `) +
    `<span class="price__discount-code-text">${renderPromoMessage(promo.message, promo.code)}</span>` +
    '</span>'
  );
}

/**
 * Price html minus every REAL promo line; placeholder reservations stay. The
 * Constructor rails use it for a sibling swatch whose own record (and so whose
 * offer scope) is unknown: the card's offer is not a promise that colourway
 * can keep, so the swatch shows the price alone.
 *
 * DOM-free (no document, no <template>) so the node harness covers it. Both
 * buildPromoLineHtml above and snippets/price-discount-code-line.liquid open a
 * real line with exactly `<span class="price__discount-code-line"`; the
 * placeholder's class attribute carries the modifier too, so it never matches.
 * The span nesting is walked to the matching close tag, so the amount / text /
 * .discount-code children go with it.
 * @param {unknown} html
 * @returns {string}
 */
export function stripPromoLineHtml(html) {
  let out = String(html ?? '');
  const open = /<span class="price__discount-code-line"(?=[\s>])/g;
  let match;
  while ((match = open.exec(out))) {
    const start = match.index;
    const tag = /<\/?span\b[^>]*>/g;
    tag.lastIndex = start;
    let depth = 0;
    let end = -1;
    let t;
    while ((t = tag.exec(out))) {
      depth += t[0].startsWith('</') ? -1 : 1;
      if (depth === 0) {
        end = t.index + t[0].length;
        break;
      }
    }
    // Unbalanced markup: leave the rest untouched rather than guess.
    if (end < 0) break;
    out = out.slice(0, start) + out.slice(end);
    open.lastIndex = start;
  }
  return out;
}

/**
 * Update an existing line element in place (slot, amount, message).
 * @param {HTMLElement} lineEl
 * @param {PromoResolution} promo
 * @param {string} formattedAmount
 */
export function applyPromoLineToElement(lineEl, promo, formattedAmount) {
  if (!lineEl || !promo) return;
  lineEl.dataset.promoSlot = String(promo.slot);
  const hide = Boolean(promo.hidePrice);
  if (hide) lineEl.dataset.promoHidePrice = 'true';
  else delete lineEl.dataset.promoHidePrice;
  if (Boolean(promo.ctaPrice) && !hide) lineEl.dataset.promoCtaPrice = 'true';
  else delete lineEl.dataset.promoCtaPrice;
  // The amount node is kept (blanked) for a hidden-price offer so a later
  // priced offer on a variant change can fill it again.
  const amountEl = lineEl.querySelector('.price__discount-code-amount');
  if (amountEl) amountEl.textContent = hide ? '' : formattedAmount;
  const textEl = lineEl.querySelector('.price__discount-code-text');
  if (textEl) textEl.innerHTML = renderPromoMessage(promo.message, promo.code);
}
